// Hollywood (Six-Six-Six / Round Robin) runner — true meta-game.
//
// 4-ball only. Round splits into three 6-hole segments — holes 1–6,
// 7–12, 13–18 — and each segment carries a different two-player
// pairing so every player partners with every other player exactly
// once. The pairings are randomised when Hollywood is first enabled
// (see defaultsWithHandicapAwareScoring in GamesModal); the user
// doesn't pick them by hand.
//
// Settlement runs ONE of four sub-games per segment, picked by the
// `subGame` option:
//
//   • strokePlay  — lowest team total (gross, no handicap). Pot mode
//                   (Bet per Player) or per-stroke head-to-head
//                   (Bet per Point), with Aggregate / Better Ball.
//   • stableford  — highest team points (Stableford scoring, net,
//                   handicap-aware, scale-aware). Pot or per-point,
//                   Aggregate / Better Ball.
//   • hiLo        — low-ball + high-ball points per hole. Pot or
//                   per-point.
//   • vegas       — digit-pair team number; differential × pointBet
//                   per hole. Flip-on-bogey / double-on-birdie
//                   modifiers honoured.
//
// The runner emits per-player cash totals plus a per-segment cash
// breakdown so the leaderboard can show each leg independently. Each
// segment also tags every player with their `team` for that 6-hole
// window — leaderboard reads that to colour the three-dot strip.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

export type Pairing = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";
export type SubGame = "strokePlay" | "stableford" | "hiLo" | "vegas";
export type ScoringMode = "net" | "gross";
export type TeamFormat = "aggregate" | "betterBall";
export type StablefordScale = "standard" | "modified" | "modifiedPga";

export interface HollywoodOptions {
  subGame?: SubGame;
  pairing1?: Pairing;
  pairing2?: Pairing;
  pairing3?: Pairing;

  // Shotgun start. When changeStartHole is on, the three 6-hole segments
  // rotate from `startHole` (1–18) and wrap past 18 back to hole 1 —
  // e.g. start on 10 → 10–15 / 16–3 / 4–9. Off → classic 1–6 / 7–12 / 13–18.
  changeStartHole?: boolean;
  startHole?: number;

  // Shared bet block (strokePlay / stableford / hiLo).
  overallBet?: number;
  betPerPoint?: number;
  useBetPerPoint?: boolean;

  // Stroke Play + Stableford.
  teamFormat?: TeamFormat;

  // Stableford.
  scoringScale?: StablefordScale;

  // Stableford + Hi-Lo.
  handicapMode?: HandicapMode;

  // Hi-Lo + Vegas.
  scoringMode?: ScoringMode;

  // Hi-Lo.
  autoPress?: boolean;

  // Vegas.
  pointBet?: number;
  flipOnBogey?: boolean;
  doubleOnBirdie?: boolean;
}

// Per-segment Hi-Lo press — same shape as HiLoPress but bounded to a
// single Hollywood segment (6 holes). Only populated when subGame is
// "hiLo" and autoPress is on.
export interface HollywoodSegmentPress {
  index: number;
  triggerHole: number;
  startHole: number;
  // Position (0-based) of the press's first hole within its segment's
  // ordered hole list. Internal — used to roll holes into the press
  // without relying on monotonic hole numbers (segments can wrap past 18).
  startIndex?: number;
  teamAPoints: number;
  teamBPoints: number;
  hasTriggered: boolean;
  netPerTeamAPlayer: number;
  netPerTeamBPlayer: number;
}

export interface HollywoodSegmentPayout {
  // 1 → segment 1 (holes 1–6), 2 → 7–12, 3 → 13–18.
  // 1-based for the player but the array is still 0-indexed below.
  net: number;
  team: "teamA" | "teamB";
  // Has the segment finished and produced a settled payout? False
  // when not enough holes have been scored yet; the leaderboard
  // renders "—" instead of a cash value.
  settled: boolean;
  // True for the winning side on a settled segment. Set even in
  // Bet-per-Point mode (the player with the higher segment net wins).
  won: boolean;
  // Hi-Lo press list for this segment. Empty unless subGame is
  // "hiLo" and autoPress is on. The leaderboard renders these
  // beneath each segment in the same "Press X — started Hole Y
  // A pts · B pts" format the standalone Hi-Lo panel uses.
  presses?: HollywoodSegmentPress[];
}

export interface HollywoodPayout {
  segments: [
    HollywoodSegmentPayout,
    HollywoodSegmentPayout,
    HollywoodSegmentPayout,
  ];
  // Cumulative cash across the three segments.
  total: number;
}

export interface HollywoodResult {
  payouts: Record<string, HollywoodPayout>;
  pairings: [Pairing, Pairing, Pairing];
  subGame: SubGame;
  hasAnyBet: boolean;
  // First/last hole numbers of each 6-hole segment, in play order. With a
  // shotgun start these wrap (e.g. [[10,15],[16,3],[4,9]]); the leaderboard
  // reads them for its segment column headers and press labels.
  segmentRanges: [[number, number], [number, number], [number, number]];
}

export interface HollywoodInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: HollywoodOptions;
}

// WHS course handicap math — duplicated locally so this runner stays
// standalone.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

function teamsFromPairing<T>(players: T[], setup: Pairing): [T[], T[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

function emptySegmentPayout(team: "teamA" | "teamB"): HollywoodSegmentPayout {
  return { net: 0, team, settled: false, won: false };
}

function emptyPayout(team: "teamA" | "teamB"): HollywoodPayout {
  return {
    segments: [
      emptySegmentPayout(team),
      emptySegmentPayout(team),
      emptySegmentPayout(team),
    ],
    total: 0,
  };
}

// Stableford net-score → points lookup. Mirrors the standalone
// Stableford runner.
function stablefordPoints(
  netStrokes: number,
  par: number,
  scale: StablefordScale,
): number {
  const diff = netStrokes - par;
  if (scale === "standard") {
    if (diff >= 2) return 0;
    return Math.min(10, 2 - diff);
  }
  if (scale === "modified") {
    if (diff >= 3) return 0;
    return Math.min(10, 3 - diff);
  }
  if (diff >= 2) return -3;
  if (diff === 1) return -1;
  if (diff === 0) return 0;
  if (diff === -1) return 2;
  if (diff === -2) return 5;
  return 8 + (-2 - diff);
}

// Pair-aggregator helper for Stroke Play / Stableford team scoring.
// Walks the segment hole-by-hole and combines the two teammates'
// per-hole values per the team format. Lower is better for strokes,
// higher is better for Stableford points — `mergeOnTie` controls
// which `min`/`max` to use in better-ball mode.
function aggregateTeamPerHole(
  segmentHoles: Hole[],
  team: Player[],
  format: TeamFormat,
  valueFor: (pid: string, hole: Hole) => number | null,
  betterBallPick: (a: number, b: number) => number,
): { total: number; settled: boolean } {
  let total = 0;
  let settled = true;
  for (const hole of segmentHoles) {
    const vals: number[] = [];
    for (const p of team) {
      const v = valueFor(p.id, hole);
      if (v == null) {
        settled = false;
        break;
      }
      vals.push(v);
    }
    if (!settled) break;
    if (format === "betterBall") {
      total += vals.reduce((acc, v) => betterBallPick(acc, v));
    } else {
      total += vals.reduce((a, b) => a + b, 0);
    }
  }
  return { total, settled };
}

// Compute Bet-per-Player vs Bet-per-Point settlement for a segment
// given each team's score and the "lower/higher is better" direction.
// Symmetric: aPayout per team-A member, bPayout per team-B member.
function settleSegmentCash(
  aScore: number,
  bScore: number,
  lowerWins: boolean,
  useBetPerPoint: boolean,
  overallBet: number,
  pointBet: number,
): { aPayout: number; bPayout: number; aWon: boolean } {
  // Direction-normalise so "diff > 0" always means A is ahead.
  const aAhead = lowerWins ? bScore - aScore : aScore - bScore;
  if (useBetPerPoint) {
    return {
      aPayout: aAhead * pointBet,
      bPayout: -aAhead * pointBet,
      aWon: aAhead > 0,
    };
  }
  // Pot mode — each member of each side antes overallBet. Pot is
  // 4 × overallBet, winning team's two members each get
  // pot/2 − stake. Ties refund (everyone nets 0).
  if (aAhead === 0) {
    return { aPayout: 0, bPayout: 0, aWon: false };
  }
  const pot = 4 * overallBet;
  const winnerShare = pot / 2;
  const aWon = aAhead > 0;
  return {
    aPayout: aWon ? winnerShare - overallBet : -overallBet,
    bPayout: aWon ? -overallBet : winnerShare - overallBet,
    aWon,
  };
}

export function runHollywood(input: HollywoodInput): HollywoodResult {
  const { course, players, scores, options } = input;
  const subGame: SubGame =
    options.subGame === "stableford" ||
    options.subGame === "hiLo" ||
    options.subGame === "vegas"
      ? options.subGame
      : "strokePlay";
  const pairings: [Pairing, Pairing, Pairing] = [
    (options.pairing1 as Pairing) || "1-2v3-4",
    (options.pairing2 as Pairing) || "1-3v2-4",
    (options.pairing3 as Pairing) || "1-4v2-3",
  ];

  // hasAnyBet is the OR of "any non-zero rate consumed by the chosen
  // sub-game". Different sub-games look at different fields.
  const hasAnyBet = (() => {
    if (subGame === "vegas") return (Number(options.pointBet) || 0) > 0;
    const useBetPerPoint = options.useBetPerPoint !== false;
    if (useBetPerPoint) return (Number(options.betPerPoint) || 0) > 0;
    return (Number(options.overallBet) || 0) > 0;
  })();

  // ── Segment hole windows ───────────────────────────────────────────
  // Three 6-hole segments. With a shotgun start (changeStartHole on) they
  // rotate from `startHole` and wrap past 18 back to hole 1; otherwise the
  // classic 1–6 / 7–12 / 13–18 split. Wrapping is only applied on a full
  // 18-hole course — anything else falls back to the linear split.
  const HOLES_PER_SEG = 6;
  const ROUND_HOLES = 18;
  const wrapEnabled =
    !!options.changeStartHole && course.holes.length === ROUND_HOLES;
  const startHoleOpt = Math.min(
    Math.max(Math.round(Number(options.startHole) || 1), 1),
    ROUND_HOLES,
  );
  const effectiveStart = wrapEnabled ? startHoleOpt : 1;
  const holesByNumber = new Map(course.holes.map((h) => [h.number, h]));
  // Ordered Hole objects per segment (skips any hole the course lacks).
  const segmentHoleArrays: Hole[][] = [0, 1, 2].map((seg) => {
    const arr: Hole[] = [];
    for (let k = 0; k < HOLES_PER_SEG; k++) {
      const num =
        ((effectiveStart - 1 + seg * HOLES_PER_SEG + k) % ROUND_HOLES) + 1;
      const h = holesByNumber.get(num);
      if (h) arr.push(h);
    }
    return arr;
  });
  // First/last hole number of each segment, for leaderboard labels.
  const segmentRanges = segmentHoleArrays.map((arr) =>
    arr.length ? [arr[0].number, arr[arr.length - 1].number] : [0, 0],
  ) as [[number, number], [number, number], [number, number]];

  // Bootstrap every player's payout shape with team teamA so the
  // leaderboard always has a slot to render even before the segments
  // tag teams. Per-segment teams overwrite this below.
  const payouts: Record<string, HollywoodPayout> = {};
  for (const p of players) payouts[p.id] = emptyPayout("teamA");

  if (players.length !== 4) {
    return { payouts, pairings, subGame, hasAnyBet: false, segmentRanges };
  }

  // Course handicap cache (used by net Stableford / Hi-Lo / Vegas).
  // The adjustment for "Play off Low" applies in Stableford and Hi-Lo;
  // Vegas uses raw stored handicaps regardless of mode flag here.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const adjustedPlayers =
    subGame === "stableford" || subGame === "hiLo"
      ? adjustHandicaps(players, options.handicapMode)
      : players;
  const courseHcpById: Record<string, number> = {};
  for (const p of adjustedPlayers) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const grossFor = (pid: string, hole: Hole): number | null => {
    const sc = (scores[pid] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    return sc.gross;
  };
  const netFor = (pid: string, hole: Hole): number | null => {
    const g = grossFor(pid, hole);
    if (g == null) return null;
    return g - strokesOnHole(courseHcpById[pid] || 0, hole);
  };

  const teamFormat: TeamFormat =
    options.teamFormat === "betterBall" ? "betterBall" : "aggregate";
  const useBetPerPoint = options.useBetPerPoint !== false;
  const overallBet = Number(options.overallBet) || 0;
  const betPerPoint = Number(options.betPerPoint) || 0;
  const vegasBet = Number(options.pointBet) || 0;
  const scoringMode: ScoringMode =
    options.scoringMode === "gross" ? "gross" : "net";
  const scoringScale: StablefordScale =
    options.scoringScale === "modifiedPga"
      ? "modifiedPga"
      : options.scoringScale === "modified"
        ? "modified"
        : "standard";
  const flipOnBogey = options.flipOnBogey !== false;
  const doubleOnBirdie = !!options.doubleOnBirdie;

  segmentHoleArrays.forEach((segmentHoles, segIdx) => {
    const pairing = pairings[segIdx];
    const [teamA, teamB] = teamsFromPairing(players, pairing);
    for (const p of teamA) payouts[p.id].segments[segIdx].team = "teamA";
    for (const p of teamB) payouts[p.id].segments[segIdx].team = "teamB";

    if (segmentHoles.length === 0) return;

    // No bet configured → tag teams, leave settled false, move on.
    if (!hasAnyBet) return;

    // ── Dispatch per sub-game ─────────────────────────────────────
    if (subGame === "vegas") {
      if (vegasBet <= 0) return;
      let aDiff = 0;
      let allScored = true;
      for (const hole of segmentHoles) {
        const aPair: number[] = [];
        const bPair: number[] = [];
        const aBogey = teamA.some((p) => {
          const g = grossFor(p.id, hole);
          return g != null && g >= hole.par + 1;
        });
        const bBogey = teamB.some((p) => {
          const g = grossFor(p.id, hole);
          return g != null && g >= hole.par + 1;
        });
        const aBirdie = teamA.some((p) => {
          const g = grossFor(p.id, hole);
          return g != null && g <= hole.par - 1;
        });
        const bBirdie = teamB.some((p) => {
          const g = grossFor(p.id, hole);
          return g != null && g <= hole.par - 1;
        });
        for (const p of teamA) {
          const v = scoringMode === "gross" ? grossFor(p.id, hole) : netFor(p.id, hole);
          if (v == null) {
            allScored = false;
            break;
          }
          aPair.push(v);
        }
        if (!allScored) break;
        for (const p of teamB) {
          const v = scoringMode === "gross" ? grossFor(p.id, hole) : netFor(p.id, hole);
          if (v == null) {
            allScored = false;
            break;
          }
          bPair.push(v);
        }
        if (!allScored) break;
        const aSorted = [...aPair].sort((x, y) => x - y);
        const bSorted = [...bPair].sort((x, y) => x - y);
        const buildNum = (sorted: number[], flip: boolean): number => {
          const lo = sorted[0];
          const hi = sorted[1];
          const leading = flip && flipOnBogey ? hi : lo;
          const trailing = flip && flipOnBogey ? lo : hi;
          return parseInt(`${leading}${trailing}`, 10);
        };
        const aNum = buildNum(aSorted, aBogey);
        const bNum = buildNum(bSorted, bBogey);
        let mult = 1;
        if (doubleOnBirdie) {
          if (aBirdie) mult *= 2;
          if (bBirdie) mult *= 2;
        }
        aDiff += (bNum - aNum) * mult;
      }
      if (!allScored) return;
      const aCash = aDiff * vegasBet;
      const bCash = -aDiff * vegasBet;
      for (const p of teamA) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = aCash;
        seg.settled = true;
        seg.won = aDiff > 0;
      }
      for (const p of teamB) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = bCash;
        seg.settled = true;
        seg.won = aDiff < 0;
      }
      return;
    }

    if (subGame === "hiLo") {
      if (useBetPerPoint && betPerPoint <= 0) return;
      if (!useBetPerPoint && overallBet <= 0) return;
      const autoPress = !!options.autoPress;
      let aPts = 0;
      let bPts = 0;
      let mainHasTriggered = false;
      // Press list scoped to THIS segment — bounded by the segment's
      // 6-hole window so a press born in segment 1 can't leak into
      // segment 2 (the pairings change between segments anyway).
      const segPresses: HollywoodSegmentPress[] = [];
      let allScored = true;
      // Indexed walk so press sequencing keys off the hole's POSITION in
      // the segment, not its raw number — a shotgun-start segment can wrap
      // past 18 (e.g. 16,17,18,1,2,3) where hole numbers aren't monotonic.
      for (let k = 0; k < segmentHoles.length; k++) {
        const hole = segmentHoles[k];
        const aScores: number[] = [];
        const bScores: number[] = [];
        for (const p of teamA) {
          const v =
            scoringMode === "gross"
              ? grossFor(p.id, hole)
              : netFor(p.id, hole);
          if (v == null) {
            allScored = false;
            break;
          }
          aScores.push(v);
        }
        if (!allScored) break;
        for (const p of teamB) {
          const v =
            scoringMode === "gross"
              ? grossFor(p.id, hole)
              : netFor(p.id, hole);
          if (v == null) {
            allScored = false;
            break;
          }
          bScores.push(v);
        }
        if (!allScored) break;
        const aLow = Math.min(...aScores);
        const aHigh = Math.max(...aScores);
        const bLow = Math.min(...bScores);
        const bHigh = Math.max(...bScores);
        // Low-ball point.
        let holeA = 0;
        let holeB = 0;
        if (aLow < bLow) holeA += 1;
        else if (bLow < aLow) holeB += 1;
        else {
          holeA += 0.5;
          holeB += 0.5;
        }
        // High-ball point.
        if (aHigh < bHigh) holeA += 1;
        else if (bHigh < aHigh) holeB += 1;
        else {
          holeA += 0.5;
          holeB += 0.5;
        }
        aPts += holeA;
        bPts += holeB;
        // Roll the hole into every press that's already started (by
        // segment position, since hole numbers can wrap).
        for (const pr of segPresses) {
          if (k < (pr.startIndex ?? 0)) continue;
          pr.teamAPoints += holeA;
          pr.teamBPoints += holeB;
        }

        // Auto-press evaluation. Skip the segment's last hole — there's
        // nothing left in the segment for a press to settle. Each game
        // (main + every existing press) gets at most ONE trigger across
        // its lifetime so a press lingering at ±2 doesn't explode. A new
        // press starts on the NEXT hole in the segment (position k + 1).
        if (autoPress && k < segmentHoles.length - 1) {
          const nextHole = segmentHoles[k + 1];
          const newPresses: HollywoodSegmentPress[] = [];
          if (!mainHasTriggered && Math.abs(aPts - bPts) === 2) {
            newPresses.push({
              index: segPresses.length + newPresses.length + 1,
              triggerHole: hole.number,
              startHole: nextHole.number,
              startIndex: k + 1,
              teamAPoints: 0,
              teamBPoints: 0,
              hasTriggered: false,
              netPerTeamAPlayer: 0,
              netPerTeamBPlayer: 0,
            });
            mainHasTriggered = true;
          }
          for (const pr of segPresses) {
            if (pr.hasTriggered) continue;
            if (k < (pr.startIndex ?? 0)) continue;
            if (Math.abs(pr.teamAPoints - pr.teamBPoints) === 2) {
              newPresses.push({
                index: segPresses.length + newPresses.length + 1,
                triggerHole: hole.number,
                startHole: nextHole.number,
                startIndex: k + 1,
                teamAPoints: 0,
                teamBPoints: 0,
                hasTriggered: false,
                netPerTeamAPlayer: 0,
                netPerTeamBPlayer: 0,
              });
              pr.hasTriggered = true;
            }
          }
          for (const np of newPresses) segPresses.push(np);
        }
      }
      if (!allScored) return;

      // Settle every press at the segment's bet rate. Each press is
      // bet-per-point (presses are conceptually a per-point construct);
      // when the round is in Bet-per-Player mode, presses settle at 0.
      let segPressACash = 0;
      let segPressBCash = 0;
      if (useBetPerPoint) {
        for (const pr of segPresses) {
          pr.netPerTeamAPlayer = (pr.teamAPoints - pr.teamBPoints) * betPerPoint;
          pr.netPerTeamBPlayer = (pr.teamBPoints - pr.teamAPoints) * betPerPoint;
          segPressACash += pr.netPerTeamAPlayer;
          segPressBCash += pr.netPerTeamBPlayer;
        }
      }

      const { aPayout, bPayout, aWon } = settleSegmentCash(
        aPts,
        bPts,
        false, // higher Hi-Lo points wins
        useBetPerPoint,
        overallBet,
        betPerPoint,
      );
      for (const p of teamA) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = aPayout + segPressACash;
        seg.settled = true;
        seg.won = aWon;
        seg.presses = segPresses;
      }
      for (const p of teamB) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = bPayout + segPressBCash;
        seg.settled = true;
        seg.won = !aWon && aPayout !== 0;
        seg.presses = segPresses;
      }
      return;
    }

    if (subGame === "stableford") {
      if (useBetPerPoint && betPerPoint <= 0) return;
      if (!useBetPerPoint && overallBet <= 0) return;
      const aRes = aggregateTeamPerHole(
        segmentHoles,
        teamA,
        teamFormat,
        (pid, hole) => {
          const net = netFor(pid, hole);
          if (net == null) return null;
          return stablefordPoints(net, hole.par, scoringScale);
        },
        Math.max,
      );
      const bRes = aggregateTeamPerHole(
        segmentHoles,
        teamB,
        teamFormat,
        (pid, hole) => {
          const net = netFor(pid, hole);
          if (net == null) return null;
          return stablefordPoints(net, hole.par, scoringScale);
        },
        Math.max,
      );
      if (!aRes.settled || !bRes.settled) return;
      const { aPayout, bPayout, aWon } = settleSegmentCash(
        aRes.total,
        bRes.total,
        false, // higher Stableford points wins
        useBetPerPoint,
        overallBet,
        betPerPoint,
      );
      for (const p of teamA) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = aPayout;
        seg.settled = true;
        seg.won = aWon;
      }
      for (const p of teamB) {
        const seg = payouts[p.id].segments[segIdx];
        seg.net = bPayout;
        seg.settled = true;
        seg.won = !aWon && aPayout !== 0;
      }
      return;
    }

    // strokePlay (default)
    if (useBetPerPoint && betPerPoint <= 0) return;
    if (!useBetPerPoint && overallBet <= 0) return;
    const aRes = aggregateTeamPerHole(
      segmentHoles,
      teamA,
      teamFormat,
      grossFor,
      Math.min, // better-ball Stroke Play = lower per-hole gross
    );
    const bRes = aggregateTeamPerHole(
      segmentHoles,
      teamB,
      teamFormat,
      grossFor,
      Math.min,
    );
    if (!aRes.settled || !bRes.settled) return;
    const { aPayout, bPayout, aWon } = settleSegmentCash(
      aRes.total,
      bRes.total,
      true, // lower strokes wins
      useBetPerPoint,
      overallBet,
      betPerPoint,
    );
    for (const p of teamA) {
      const seg = payouts[p.id].segments[segIdx];
      seg.net = aPayout;
      seg.settled = true;
      seg.won = aWon;
    }
    for (const p of teamB) {
      const seg = payouts[p.id].segments[segIdx];
      seg.net = bPayout;
      seg.settled = true;
      seg.won = !aWon && aPayout !== 0;
    }
  });

  for (const p of players) {
    const pay = payouts[p.id];
    pay.total = pay.segments.reduce((s, seg) => s + seg.net, 0);
  }

  return { payouts, pairings, subGame, hasAnyBet, segmentRanges };
}

// Fair-rotation shuffle. Returns one of the six orderings of the
// three pairings that together form a complete round-robin (every
// player partners with every other player exactly once). Called from
// GamesModal's defaults helper when Hollywood is first enabled.
export function shuffleRoundRobinPairings(): [Pairing, Pairing, Pairing] {
  const all: Pairing[] = ["1-2v3-4", "1-3v2-4", "1-4v2-3"];
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  return [all[0], all[1], all[2]];
}
